import { useRouter } from 'next/router';
import { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { getUserApiMethod, ensureCsrfToken } from '../lib/api/public';
import { Alert, Button, Box } from '@mui/material';
import { User } from 'components/types';
import { formatUserDate } from 'components/utils';

export type ContextType = {
  user: User | null;
  isAdmin: boolean;
  loadingUser: boolean;
  redirectToLogin: boolean;
  fetchUser: () => Promise<void>;
  drawerCollapsed: boolean;
  shouldHideInfoSections: () => boolean | string[] | string;
  setDrawerCollapsed: (collapsed: boolean) => void;
  renderSubscriptionInfo?: (() => React.ReactNode) | undefined;
  isTrialExpired?: boolean;
};

const SessionContext = createContext<ContextType | undefined>(undefined);

export const useSession = () => {
  const context = useContext(SessionContext);

  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }

  return context;
};

type SessionProviderProps = {
  children: ReactNode;
};

export const SessionProvider = ({ children }: SessionProviderProps) => {
  const router = useRouter();
  const { query } = router;

  const [user, setUser] = useState<User | null>(null);
  const [loadingUser, setLoadigUser] = useState(true);
  const [redirectToLogin, setRedirectToLogin] = useState(false);

  // Initialize from localStorage, defaulting to false if not found
  const [drawerCollapsed, setDrawerCollapsedState] = useState(() => {
    // Only run this on client-side to avoid SSR issues
    if (typeof window !== 'undefined') {
      const savedState = localStorage.getItem('drawerCollapsed');
      return savedState === 'true';
    }

    return false;
  });

  // Wrapper for setDrawerCollapsed that also updates localStorage
  const setDrawerCollapsed = (collapsed: boolean) => {
    setDrawerCollapsedState(collapsed);
    if (typeof window !== 'undefined') {
      localStorage.setItem('drawerCollapsed', String(collapsed));
    }
  };

  const loadCsrfToken = async () => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      await ensureCsrfToken();
    } catch {
      // Silently ignore hydration issues; requests will retry later.
    }
  };

  const fetchUser = async () => {
    try {
      const res = (await getUserApiMethod()) as { user: { tablePreferences: Record<string, unknown> } & User };

      if (res.user) {
        setUser(res.user);

        localStorage.setItem('tablePreferences', JSON.stringify(res.user.tablePreferences || {}));

        // Store user's date/time preferences
        localStorage.setItem('userDateFormat', res.user.dateFormat || 'MMM DD, YYYY');
        localStorage.setItem('userTimeFormat', res.user.timeFormat || '12h');
      }

      await loadCsrfToken();

      if (!res.user) {
        setRedirectToLogin(true);
      }
    } catch (err) {
      if (err instanceof Error && err.message && err.message.includes('Account not found')) {
        void router.push('/not-found');
        return;
      } else {
        setRedirectToLogin(true);
      }
    } finally {
      setLoadigUser(false);
    }
  };

  useEffect(() => {
    void fetchUser();
  }, []);

  const shouldHideInfoSections = () => {
    return (
      !user ||
      router.pathname.includes('/meetings') ||
      query.accountId ||
      router.pathname.includes('/system-status') ||
      router.pathname.includes('/unsubscribe') ||
      query.observerLink ||
      query.analyticsPublicLink ||
      query.view === 'interview'
    );
  };

  const account = user?.account;
  const { trial, isTrialExpired } = account || {};

  const renderSubscriptionInfo = () => {
    if (shouldHideInfoSections()) {
      return null;
    }

    if (user && user.role !== 'superAdmin') {
      return null;
    }

    if (!account) {
      return null;
    }

    const trialEndDate = trial?.endDate;

    const commonStyle = {
      margin: '0 8px 8px 8px',
      width: '100%',
      backgroundColor: '#2e1b05',
      color: 'rgba(253, 225, 194, 0.6)',
      display: drawerCollapsed ? 'none' : { xs: 'none', sm: 'block' },
    };

    if (account.isCancelSubscriptionEnded) {
      return (
        <Alert sx={commonStyle} icon={false} color="warning">
          Subscription ended
          <Button
            sx={{ marginTop: '10px', width: '100%' }}
            size="medium"
            variant="contained"
            color="warning"
            onClick={() => {
              void router.push('/settings/billing');
            }}
          >
            Update
          </Button>
        </Alert>
      );
    }

    if (account.stripeSubscription) {
      return null;
    }

    return (
      <Alert
        sx={{
          ...commonStyle,
          p: 1,
          '& .MuiAlert-message': { p: 0, width: '100%' },
        }}
        icon={false}
        color="warning"
      >
        <Box sx={{ fontSize: 12, lineHeight: 1.4, textAlign: 'center', whiteSpace: 'nowrap' }}>
          {isTrialExpired ? (
            'Trial expired'
          ) : (
            <>
              Trial ends <strong>{trialEndDate ? formatUserDate(trialEndDate) : ''}</strong>
            </>
          )}
        </Box>
        <Button
          sx={{ mt: 0.75, width: '100%', minHeight: 0, py: 0.25, fontSize: 12 }}
          size="small"
          variant="contained"
          color="warning"
          onClick={() => {
            void router.push('/settings/billing');
          }}
        >
          Upgrade
        </Button>
      </Alert>
    );
  };

  return (
    <SessionContext.Provider
      value={{
        user,
        isAdmin: ['superAdmin', 'admin'].includes(user?.role || ''),
        loadingUser,
        fetchUser,
        drawerCollapsed,
        setDrawerCollapsed,
        renderSubscriptionInfo,
        redirectToLogin,
        shouldHideInfoSections,
        isTrialExpired,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};
